import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrFilter } from '@nostrify/nostrify';
import { parseVideoEvent, type VideoData } from '@/lib/videoTypes';

/**
 * Fetch a single video event by id (note/nevent) or address (naddr).
 */
export function useVideoById(query: { id?: string; kind?: number; pubkey?: string; identifier?: string } | null) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['video', query?.id, query?.kind, query?.pubkey, query?.identifier],
    queryFn: async ({ signal }): Promise<VideoData | null> => {
      if (!query) return null;

      const filters: NostrFilter[] = [];
      if (query.identifier !== undefined && query.pubkey && query.kind) {
        // Addressable lookup — author filter is critical (d tag alone is
        // not a trust boundary)
        filters.push({
          kinds: [query.kind],
          authors: [query.pubkey],
          '#d': [query.identifier],
          limit: 1,
        });
      } else if (query.id) {
        filters.push({ ids: [query.id], limit: 1 });
      } else {
        return null;
      }

      const events = await nostr.query(filters, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]),
      });

      if (events.length === 0) return null;

      // For addressable events pick the newest version
      const sorted = events.sort((a, b) => b.created_at - a.created_at);
      return parseVideoEvent(sorted[0]);
    },
    enabled: !!query,
    staleTime: 30 * 1000,
  });
}
